import React from "react"
import { graphql, useLazyLoadQuery } from "react-relay"

import { PhaseItemTooltipBudgetSectionQuery } from "~/ProjectPlanner/Phases/__generated__/PhaseItemTooltipBudgetSectionQuery.graphql"

import { getCalculateByDayStrategy } from "@runn/calculations"
import { RateCard } from "~/forms/ProjectBudgetForm/types"
import { formatCurrency } from "~/helpers/CurrencyHelper"
import { calculateBudgetWithRemainingByPhase } from "~/helpers/project-helpers"

type Props = {
  projectId: number
  phase: {
    id: number
    revenue_budget: number
  }
  rateCards: readonly RateCard[]
}

const PhaseItemTooltipBudgetSection = ({
  projectId,
  phase,
  rateCards,
}: Props) => {
  const query = useLazyLoadQuery<PhaseItemTooltipBudgetSectionQuery>(
    graphql`
      query PhaseItemTooltipBudgetSectionQuery($projectId: Int!, $phaseId: Int!) {
        projects_by_pk(id: $projectId) {
          id
          is_template
          budget_method
          project_rates {
            id
            role_id
            rate: rate_private
          }
          rate_card {
            id
            name
            blended_rate: blended_rate_private
            blended_rate_card
            role_charge_out_rates {
              role_id
              rate_card_id
              charge_out_rate: charge_out_rate_private
            }
          }
          project_roles {
            id
            role_id
            estimated_minutes
          }
          phases {
            id
            revenue_budget: revenue_budget_private
          }
          phase_roles(where: { phase_id: { _eq: $phaseId } }) {
            id
            phase_id
            role_id
            estimated_minutes
          }
          summary {
            role_id
            minutes_phase_id
            total_billable_minutes
            actual_phase_id
          }
          other_costs(order_by: { date: asc }) {
            id
            date: date_runn
            charge: charge_private
            cost: cost_private
            phase_id
          }
          pricing_model
          total_budget: total_budget_private
          actuals {
            id
            date: date_runn
            billable_minutes
            nonbillable_minutes
            person_id
            project_id
            role_id
            phase_id
            workstream_id
          }
          assignments {
            id
            start_date: start_date_runn
            end_date: end_date_runn
            minutes_per_day
            person_id
            project_id
            role_id
            is_billable
            non_working_day
            phase_id
            workstream_id
          }
          members {
            id
            person {
              id
              is_placeholder
              contracts {
                start_date: start_date_runn
                end_date: end_date_runn
                cost: cost_private
                padded_cost: cost_padded_private
              }
              time_offs {
                id
                start_date: start_date_runn
                end_date: end_date_runn
                minutes_per_day
                leave_type
              }
            }
          }
        }
      }
    `,
    { projectId: projectId, phaseId: phase.id },
  )

  const project = query.projects_by_pk

  if (!project) {
    return null
  }

  // CTO feature flag is now turned on for all customers
  const calculateByDay = getCalculateByDayStrategy(true)

  // for considering partial time off
  const combinedHours = calculateByDay({
    actuals: project.actuals,
    assignments: project.assignments,
    rates: project.project_rates,
    people: project.members.map((member) => member.person),
    isTemplate: project.is_template,
  })

  const { budget, budgetRemaining } = calculateBudgetWithRemainingByPhase(
    project,
    phase,
    combinedHours,
  )

  return (
    <>
      <div>
        <b>Budget:</b> {formatCurrency(budget ?? 0)}
      </div>
      <div>
        <b>Remaining:</b> {formatCurrency(budgetRemaining ?? 0)}
      </div>
    </>
  )
}

export default PhaseItemTooltipBudgetSection
